import { IOpsClinician, IOpsClinicianRow, IWeeklySessions } from 'model/ops';
import { DT_DISCIPLINE_ORDER } from './clinicDtCapacity';
import { formatProviderName } from 'utils/format/providerName';

/**
 * AXON-3585: shaping the single-clinic DT capacity table.
 *
 * Pure functions over the query result, so the ordering, nesting and summing
 * rules can be tested without rendering.
 *
 * Everything is built from per-clinician weekly shapes, summed upward. That is
 * what makes "the five weekday figures sum to Open sessions" true of every row
 * by construction rather than a rule to be policed — and it keeps the flooring
 * of open slots where the backend put it, on the individual clinician.
 */

/** The five weekdays the table shows, in column order. */
export const WEEKDAY_KEYS: (keyof IWeeklySessions)[] = [
  'mon',
  'tues',
  'wed',
  'thurs',
  'fri'
];

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * The assistant grades known today, in the order they should appear.
 *
 * Only an ordering hint: which clinicians are assistants comes from the
 * backend's `isAssistantGrade`, and a grade not listed here still draws its own
 * sub-row rather than disappearing. A hardcoded membership test would silently
 * drop the next assistant grade someone adds.
 */
export const DT_ASSISTANT_GRADE_ORDER = ['COTA', 'SLPA'];

const gradeRank = (grade: string) => {
  const known = DT_ASSISTANT_GRADE_ORDER.indexOf(grade);
  return known === -1 ? DT_ASSISTANT_GRADE_ORDER.length : known;
};

/**
 * Every grade actually held in this discipline, in display order: therapist
 * grades first, then assistants.
 *
 * The discipline's own abbreviation leads — OT before any other therapist grade
 * under OT — so the row named after the discipline is the one directly beneath
 * it, and a reader meets the grades in the order they'd name them.
 */
const gradesIn = (
  clinicians: IOpsClinician[],
  discipline: string
): string[] => {
  const seen = new Map<string, boolean>();
  clinicians.forEach(clinician =>
    seen.set(clinician.specialtyAbbreviation, !!clinician.isAssistantGrade)
  );

  const rank = (grade: string) => {
    if (seen.get(grade)) return 2; // assistants last
    return grade === discipline ? 0 : 1; // the discipline's own grade first
  };

  return Array.from(seen.keys()).sort(
    (a, b) =>
      rank(a) - rank(b) ||
      gradeRank(a) - gradeRank(b) ||
      a.localeCompare(b)
  );
};

const weekdaysOf = (weekly?: IWeeklySessions | null): number[] =>
  WEEKDAY_KEYS.map(key => Number(weekly?.[key]) || 0);

const addInto = (into: number[], from: number[]) =>
  into.map((value, index) => value + (from[index] || 0));

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);

/**
 * Totals for a set of clinicians.
 *
 * Note that `open` is summed from per-clinician figures the backend has already
 * floored, and is NOT re-derived as total minus booked. Those differ whenever
 * someone is booked past their own template: they have no openings rather than
 * negative ones, so the row's Open can exceed Total minus Booked. Both figures
 * are honest; the subtraction is what stops being valid across a mixed row.
 */
/**
 * Template and open totals are the clinician's own `total` when a weekly session
 * limit is stored (the backend sets it to the limit while the weekday figures
 * stay on the template). Without one they are the five weekday figures added up,
 * as before — `total` also carries Saturday and Sunday, which this table omits.
 */
const weeklyTotalOf = (
  weekly: IWeeklySessions | null | undefined,
  hasLimit: boolean
): number =>
  hasLimit ? Number(weekly?.total) || 0 : sum(weekdaysOf(weekly));
const rollUp = (clinicians: IOpsClinician[]) => {
  const empty = [0, 0, 0, 0, 0];
  return clinicians.reduce(
    (totals, clinician) => ({
      template:
        totals.template +
        weeklyTotalOf(clinician.templateSessions, !!clinician.hasWeeklyLimit),
      booked: totals.booked + sum(weekdaysOf(clinician.bookedSessions)),
      open:
        totals.open +
        weeklyTotalOf(clinician.openSessions, !!clinician.hasWeeklyLimit),
      weekdayOpen: addInto(totals.weekdayOpen, weekdaysOf(clinician.openSessions))
    }),
    { template: 0, booked: 0, open: 0, weekdayOpen: empty }
  );
};

const groupRow = (
  key: string,
  depth: 0 | 1,
  discipline: string,
  label: string,
  clinicians: IOpsClinician[]
): IOpsClinicianRow => {
  const totals = rollUp(clinicians);
  return {
    key,
    depth,
    discipline,
    label,
    staff: clinicians.length,
    // A grade row is about one grade; a discipline row is not, and is named by
    // `discipline` instead.
    specialty: depth === 1 ? label : undefined,
    totalSessions: totals.template,
    bookedSessions: totals.booked,
    openSessions: totals.open,
    weekdayOpen: totals.weekdayOpen
  };
};

/**
 * "Beth Johnson, COTA" — the Provider Roster's own format
 * (`${displayName}, ${specialty}`) minus its clinic suffix, which is
 * the one thing a reader already knows inside a single clinic's view. The same
 * person should not read one way on the Providers tab and another here.
 */
export const clinicianNameOf = (clinician: IOpsClinician) =>
  `${formatProviderName(clinician)}, ${clinician.specialtyAbbreviation}`.trim();

/**
 * "Supervised by Iris Bellamy", or nothing.
 *
 * The grade used to lead this line; it moved into the name above, and repeating
 * it here would say the same thing twice on consecutive lines.
 */
const sublabelOf = (clinician: IOpsClinician) =>
  clinician.supervisorName
    ? `Supervised by ${clinician.supervisorName}`
    : undefined;

const clinicianRow = (clinician: IOpsClinician): IOpsClinicianRow => {
  const open = weekdaysOf(clinician.openSessions);
  return {
    key: `clinician-${clinician.providerId}`,
    depth: 2,
    discipline: clinician.discipline,
    label: clinicianNameOf(clinician),
    sublabel: sublabelOf(clinician),
    providerId: clinician.providerId,
    totalSessions: weeklyTotalOf(
      clinician.templateSessions,
      !!clinician.hasWeeklyLimit
    ),
    bookedSessions: sum(weekdaysOf(clinician.bookedSessions)),
    openSessions: weeklyTotalOf(
      clinician.openSessions,
      !!clinician.hasWeeklyLimit
    ),
    weekdayOpen: open,
    isUnconfigured: !clinician.templateSessions,
    // A template with no booked figure ever computed reads as an entirely free
    // week, which is the one way this table can mislead in the direction that
    // causes harm — a scheduler placing a child into a slot that is already
    // taken. Distinguished from a genuine zero rather than left to look alike.
    hasNoBookedData: !!clinician.templateSessions && !clinician.bookedSessions
  };
};

const byName = (a: IOpsClinician, b: IOpsClinician) =>
  formatProviderName(a).localeCompare(formatProviderName(b));

/**
 * The table's rows, deepest last: each discipline, then the grades reporting
 * under it, then the clinicians holding each grade.
 *
 * Where a discipline holds more than one grade, every grade gets a row of its
 * own — OT beside COTA, not COTA alone with the therapists left implicit — so
 * the two subtotals can be read against each other and against the discipline
 * row above them.
 *
 * The one case with no grade row is a discipline whose single grade is its own
 * name — an OT staffed only by OTs. That row would carry the same label and the
 * same figures as the discipline row directly above it. A lone grade under a
 * different name (an OT staffed only by COTAs) still gets its row, because that
 * is a fact about the clinic worth seeing.
 */
export const buildClinicianRows = (
  clinicians: IOpsClinician[]
): IOpsClinicianRow[] => {
  const present = (clinicians || []).filter(clinician => clinician.discipline);
  const rows: IOpsClinicianRow[] = [];

  DT_DISCIPLINE_ORDER.forEach(discipline => {
    const inDiscipline = present.filter(
      clinician => clinician.discipline === discipline
    );
    if (inDiscipline.length === 0) return;

    rows.push(
      groupRow(
        `discipline-${discipline}`,
        0,
        discipline,
        discipline,
        inDiscipline
      )
    );

    const grades = gradesIn(inDiscipline, discipline);
    const labelled = !(grades.length === 1 && grades[0] === discipline);

    grades.forEach(grade => {
      const holders = inDiscipline
        .filter(clinician => clinician.specialtyAbbreviation === grade)
        .sort(byName);
      if (holders.length === 0) return;

      if (labelled) {
        rows.push(
          groupRow(`grade-${discipline}-${grade}`, 1, discipline, grade, holders)
        );
      }
      holders.forEach(clinician => rows.push(clinicianRow(clinician)));
    });
  });

  return rows;
};
